import { useQuery } from "@tanstack/react-query";
import { startOfWeek, format } from "date-fns";
import { supabase } from "@/lib/supabase";
import { useAuth } from "./useAuth";
import type { GiftPoolRow, GiftScoreRow } from "@/types/database";

function currentWeekStart(): string {
  return format(startOfWeek(new Date(), { weekStartsOn: 1 }), "yyyy-MM-dd");
}

export function useCurrentPool() {
  const week = currentWeekStart();
  return useQuery({
    queryKey: ["gift-pool", week],
    queryFn: async (): Promise<GiftPoolRow | null> => {
      const { data, error } = await supabase
        .from("gift_pools")
        .select("*")
        .eq("week_start", week)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });
}

export function useLeaderboard(limit = 100) {
  const week = currentWeekStart();
  return useQuery({
    queryKey: ["gift-leaderboard", week, limit],
    refetchInterval: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("gift_weekly_scores")
        .select("*, users(first_name, avatar_url)")
        .eq("week_start", week)
        .order("points", { ascending: false })
        .limit(limit);
      if (error) throw error;
      return (data ?? []) as Array<GiftScoreRow & { users: { first_name: string | null; avatar_url: string | null } | null }>;
    },
  });
}

export function useMyGiftScore() {
  const { user } = useAuth();
  const week = currentWeekStart();
  return useQuery({
    queryKey: ["gift-score", user?.id, week],
    enabled: !!user?.id,
    queryFn: async (): Promise<GiftScoreRow | null> => {
      const { data, error } = await supabase
        .from("gift_weekly_scores")
        .select("*")
        .eq("user_id", user!.id)
        .eq("week_start", week)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });
}
